import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PerformanceStatus } from '../../../../models/performance.model';

export interface AdvanceStageDescriptor {
  index: number;
  code: string;
  label: string;
  sub: string;
  status: PerformanceStatus;
  activeLabel?: string;
}

export type AdvanceStageDirection = 'forward' | 'back';

export interface AdvanceStageResult {
  fromStatus: PerformanceStatus;
  toStatus: PerformanceStatus;
  fromLabel: string;
  toLabel: string;
  fromIndex: number;
  toIndex: number;
  direction: AdvanceStageDirection;
}

interface ChecklistItem {
  text: string;
  checked: boolean;
}

@Component({
  selector: 'app-advance-stage',
  templateUrl: './advance-stage.component.html',
  standalone: false,
  styleUrls: ['./advance-stage.component.scss']
})
export class AdvanceStageComponent implements OnInit {
  @Input() fromStage!: AdvanceStageDescriptor;
  @Input() toStage!: AdvanceStageDescriptor;
  @Input() totalStages: number = 5;
  @Input() performanceSubtitle: string = '';
  @Input() currentUserEmail: string = '';
  @Input() saving: boolean = false;
  @Input() saveError: string = '';
  @Input() direction: AdvanceStageDirection = 'forward';

  @Output() confirmed = new EventEmitter<AdvanceStageResult>();
  @Output() cancelled = new EventEmitter<void>();

  checklist: ChecklistItem[] = [];
  typedConfirmation = '';

  ngOnInit(): void {
    this.checklist = this.buildChecklist().map((text) => ({
      text,
      checked: false
    }));
  }

  toggleItem(item: ChecklistItem) {
    if (this.saving) { return; }
    item.checked = !item.checked;
  }

  allChecked(): boolean {
    return this.checklist.length > 0 && this.checklist.every((c) => c.checked);
  }

  get isBack(): boolean {
    return this.direction === 'back';
  }

  get expectedConfirmationWord(): string {
    return this.isBack ? 'reverse stage' : 'advance stage';
  }

  get title(): string {
    return this.isBack ? 'Roll back to previous stage?' : 'Proceed to next stage?';
  }

  get confirmLabel(): string {
    if (this.saving) { return this.isBack ? 'Rolling back…' : 'Advancing…'; }
    return 'Confirm';
  }

  typedConfirmationOk(): boolean {
    return this.typedConfirmation.trim().toLowerCase() === this.expectedConfirmationWord;
  }

  canConfirm(): boolean {
    if (this.saving) { return false; }
    if (!this.allChecked()) { return false; }
    if (!this.typedConfirmationOk()) { return false; }
    return true;
  }

  cancel() {
    if (this.saving) { return; }
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.cancel();
    }
  }

  confirm() {
    if (!this.canConfirm()) { return; }
    this.confirmed.emit({
      fromStatus: this.fromStage.status,
      toStatus: this.toStage.status,
      fromLabel: `${this.fromStage.label} ${this.fromStage.sub}`.trim(),
      toLabel: `${this.toStage.label} ${this.toStage.sub}`.trim(),
      fromIndex: this.fromStage.index,
      toIndex: this.toStage.index,
      direction: this.direction
    });
  }

  private buildChecklist(): string[] {
    if (this.isBack) {
      return [
        `I understand the performance will return to "${this.toStage.label} ${this.toStage.sub}".`,
        'I have confirmed any data captured in the current stage will remain but the lifecycle status will revert.'
      ];
    }
    switch (this.fromStage?.code) {
      case 'EL':
        return [
          'I have reviewed every event in the listing',
          'Any events not in this round have been marked REMOVE'
        ];
      case 'SR':
        return [
          'Every event/plan that should scan has been placed in a gate scan window',
          'Scan open and close times are set for every window',
          'Events/plans that must not scan at a gate have been left out of that gate'
        ];
      case 'TC':
        return [
          'Per-gate expected results set for every barcode',
          'Extended tests added for any custom barcodes (VIP, staff, back-of-house)',
          'Client has signed off on the event listing and gate expectations'
        ];
      case 'TM1':
        return [
          'TM1 entry configuration is complete for every gate'
        ];
      default:
        return ['Confirm advancing to the next stage'];
    }
  }
}
